import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import Label from '../../components/foundations/Label';
import withTranslate from '../../containers/widgets/withTranslate';
import { Button } from '../../components/widgets';
import { ALERT_LEVELS, CAP_DISPLAY_NAMES, CAP_TYPES } from '../consts';
import css from './CapsList.css';

const USAGE_TAG_CLASS_MAP = {
    [ALERT_LEVELS.BLOCKED]: css.tagError,
    [ALERT_LEVELS.HARSH_WARNING]: css.tagError,
    [ALERT_LEVELS.MODERATE_WARNING]: css.tagWarning,
    [ALERT_LEVELS.GENTLE_WARNING]: css.tagWarning,
};

const USAGE_TAG_TEXT_MAP = {
    [ALERT_LEVELS.BLOCKED]: 'STATIC.PAGES.ACCOUNT_PLAN.ALERTS.BLOCKED_USAGE',
    [ALERT_LEVELS.HARSH_WARNING]: 'STATIC.PAGES.ACCOUNT_PLAN.ALERTS.EXTRA_HIGH_USAGE',
    [ALERT_LEVELS.MODERATE_WARNING]: 'STATIC.PAGES.ACCOUNT_PLAN.ALERTS.MODERATE_USAGE',
    [ALERT_LEVELS.GENTLE_WARNING]: 'STATIC.PAGES.ACCOUNT_PLAN.ALERTS.HIGH_USAGE',
};

const getUsageTagClassName = alertLevel => {
    return USAGE_TAG_CLASS_MAP[alertLevel] || css.tagHealthy;
};

const getUsageTagText = alertLevel => {
    return USAGE_TAG_TEXT_MAP[alertLevel] || 'STATIC.PAGES.ACCOUNT_PLAN.ALERTS.HEALTHY_USAGE';
};

const formatPercent = value => `${value.toFixed(2)}`;

const CapsList = ({
    alerts = [],
    caps = {},
    translate = () => '',
    defaultExpanded = false,
    isPaused = false,
    onAuditEvents,
    isAuditing = false,
    actualEvents,
    eventsRunRatePercent,
}) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    const alertMap = alerts.reduce((acc, alert) => {
        acc[alert.metric] = alert;
        return acc;
    }, {});

    const eventCap = caps[CAP_TYPES.EVENT];
    // Event-usage lines are opt-in per org (backend omits the fields when off), so render each
    // only when its value is present rather than gating on an option the frontend never sees.
    const hasActualEvents = actualEvents !== undefined && actualEvents !== null;
    const hasRunRate = eventsRunRatePercent !== undefined && eventsRunRatePercent !== null;
    const dailyRunRateTarget = eventCap ? Math.round(eventCap / 365) : 0;
    const currentRunRate = hasRunRate ? Math.round((eventsRunRatePercent / 100) * (eventCap / 365)) : 0;
    // eventsRunRatePercent is a ratio vs target (100 = on target); the label shows the distance
    // from target, e.g. a 150% run rate is "50% over target" and 80% is "20% below target".
    const isOverTarget = eventsRunRatePercent > 100;
    const runRateVariancePercent = Math.abs(eventsRunRatePercent - 100);

    const renderCapItem = (capKey, capValue) => {
        // Check if there's an alert for this cap type (metric matches cap key)
        const alert = alertMap[capKey];
        const alertLevel = alert?.alertLevel || ALERT_LEVELS.NONE;
        const capName = translate(CAP_DISPLAY_NAMES[capKey] || capKey);
        const isBlocked = alertLevel === ALERT_LEVELS.BLOCKED;

        return (
            <div key={capKey} className={css.capItem}>
                <span className={classNames(css.capName, { [css.blockedText]: isBlocked })}>
                    {capName}: <span className={css.capValue}>{capValue.toLocaleString()}</span>
                </span>
                {!isPaused && (
                    <span className={classNames(css.usageTag, getUsageTagClassName(alertLevel))}>
                        <Icon
                            name="activity-heartbeat"
                            size={IconSizes.X_SMALL}
                            className={css.tagIcon}
                            interactive={false}
                        />
                        <Translate id={getUsageTagText(alertLevel)} />
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className={css.capsListContainer}>
            <button
                type="button"
                className={css.toggleButton}
                onClick={() => setIsExpanded(prevExpanded => !prevExpanded)}
            >
                <Icon
                    name={isExpanded ? 'chevron-up' : 'chevron-down'}
                    size={IconSizes.X_SMALL}
                    className={css.toggleIcon}
                />
                <Translate
                    id={isExpanded ? 'STATIC.PAGES.ACCOUNT_PLAN.SHOW_LESS' : 'STATIC.PAGES.ACCOUNT_PLAN.SHOW_MORE'}
                />
            </button>
            {isExpanded && (
                <div className={css.capsContent}>
                    <Label text="STATIC.PAGES.ACCOUNT_PLAN.PLAN_INCLUDES_CAPS" className={css.capsTitle} />
                    <div className={css.capsList}>
                        {Object.entries(caps).map(([capKey, capValue]) => renderCapItem(capKey, capValue))}
                    </div>
                    {eventCap && hasActualEvents ? (
                        <div className={css.capItem}>
                            <span className={css.capName}>
                                {translate('STATIC.PAGES.ACCOUNT_PLAN.EVENTS_USED_YTD')}
                                {': '}
                                <span className={css.capValue}>{actualEvents.toLocaleString()}</span>
                                {' - '}
                                <span className={css.positive}>
                                    {translate('STATIC.PAGES.ACCOUNT_PLAN.PERCENT_OF_CAP', {
                                        percent: formatPercent((actualEvents / eventCap) * 100),
                                    })}
                                </span>
                            </span>
                        </div>
                    ) : null}
                    {eventCap ? (
                        <div className={css.capItem}>
                            <span className={css.capName}>
                                {translate('STATIC.PAGES.ACCOUNT_PLAN.DAILY_RUN_RATE_TARGET')}
                                {': '}
                                <span className={css.capValue}>{dailyRunRateTarget.toLocaleString()}</span>
                            </span>
                        </div>
                    ) : null}
                    {eventCap && hasRunRate ? (
                        <div className={css.capItem}>
                            <span className={css.capName}>
                                {translate('STATIC.PAGES.ACCOUNT_PLAN.CURRENT_RUN_RATE')}
                                {': '}
                                <span className={css.capValue}>{currentRunRate.toLocaleString()}</span>
                                {' - '}
                                <span className={isOverTarget ? css.negative : css.positive}>
                                    {translate(
                                        isOverTarget
                                            ? 'STATIC.PAGES.ACCOUNT_PLAN.PERCENT_OVER_TARGET'
                                            : 'STATIC.PAGES.ACCOUNT_PLAN.PERCENT_BELOW_TARGET',
                                        { percent: formatPercent(runRateVariancePercent) }
                                    )}
                                </span>
                            </span>
                        </div>
                    ) : null}
                    {eventCap && onAuditEvents ? (
                        <Button
                            type="secondary"
                            className={css.auditButton}
                            text="STATIC.PAGES.ACCOUNT_PLAN.BUTTONS.AUDIT_EVENTS"
                            onClick={onAuditEvents}
                            disabled={isAuditing}
                            showSpinner={isAuditing}
                        />
                    ) : null}
                </div>
            )}
        </div>
    );
};

CapsList.propTypes = {
    alerts: PropTypes.arrayOf(
        PropTypes.shape({
            metric: PropTypes.string.isRequired,
            alertLevel: PropTypes.number.isRequired,
        })
    ),
    caps: PropTypes.objectOf(PropTypes.number),
    translate: PropTypes.func,
    defaultExpanded: PropTypes.bool,
    isPaused: PropTypes.bool,
    onAuditEvents: PropTypes.func,
    isAuditing: PropTypes.bool,
    actualEvents: PropTypes.number,
    eventsRunRatePercent: PropTypes.number,
};

export default withTranslate(CapsList);
